import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ToastController, ActionSheetController, AlertController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { version } from './global.config';
import { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { User } from './signin/credentials';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import { MapNavigationService } from './utils/map-top-toolbar/map-navigation.service';
import { SafeAreaService } from './utils/safe-area/safe-area.service';
import { VideoOrientationService } from './utils/video-orientation/video-orientation.service';
import { BootSplashService } from './utils/boot-splash/boot-splash.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {

  version: string = version;
  currentUser: User | null;
  backButtonSubscription: Subscription;
  interval: any;
  showMapToolbar = false;

  private routeAllowsToolbar = false;
  private toolbarHidden = false;
  private bootSplashDismissed = false;
  private splashVisible = false;
  private readonly splashStartedAt = Date.now();
  /** Cut to login as soon as it is painted — no extra blue hold. */
  private readonly minSplashMs = 0;
  private readonly mapToolbarRoutes = ['/client', '/path', '/stop', '/mileage', '/overspeed', '/video-playback'];
  private destroy$ = new Subject<void>();

  constructor(
    private toastController: ToastController,
    public actionSheetCtrl: ActionSheetController,
    public alertController: AlertController,
    private translate: TranslateService,
    private router: Router,
    private mapNavigationService: MapNavigationService,
    private safeAreaService: SafeAreaService,
    private videoOrientationService: VideoOrientationService,
    private bootSplash: BootSplashService,
  ) {
    // Capacitor shows splash at launch automatically - don't show again

    this.safeAreaService.start();
    void this.initStatusBar(true);
    void this.videoOrientationService.lockAppPortrait();
    this.mapNavigationService.toolbarHidden$.subscribe((hidden) => {
      this.toolbarHidden = hidden;
      this.refreshToolbar();
    });
    this.translate.addLangs(['en', 'fr', 'ar']);
    this.translate.setDefaultLang('fr');

    const savedLang = localStorage.getItem('lang');
    const browserLang = this.translate.getBrowserLang();
    const lang = (savedLang && ['en', 'fr', 'ar'].includes(savedLang))
      ? savedLang
      : (browserLang && browserLang.match(/en|fr|ar/) ? browserLang : 'fr');
    this.translate.use(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;

    this.currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    this.updateToolbarVisibility(this.router.url);
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        const url = (e as NavigationEnd).urlAfterRedirects;
        this.updateToolbarVisibility(url);
      });

    this.splashVisible = true;
    void this.waitForLoginThenDismissSplash();
  }

  private async waitForLoginThenDismissSplash(): Promise<void> {
    // Capacitor shows splash for 3s, then auto-hides with 500ms fade (3.5s total)
    // After fade completes, switch HTML/CSS to white background
    setTimeout(() => {
      document.body.classList.add('splash-hidden');
    }, 3500);
  }

  private ensureBootSplashDom(): void {
    // Keep the native splash only. No custom HTML splash/white overlay is injected here,
    // because that creates the blank gap between the splash and the login screen.
  }

  private async hideNativeSplash(): Promise<void> {
    // launchAutoHide is now true, so Capacitor handles hiding automatically
    // No manual hide needed
  }

  private async initStatusBar(bootSplash = false): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await StatusBar.setOverlaysWebView({ overlay: false });

      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setStyle({
          style: bootSplash ? Style.Dark : Style.Light
        });

        await StatusBar.setBackgroundColor({
          color: '#226597',
        });
      } else {
        await StatusBar.setStyle({
          style: bootSplash ? Style.Dark : Style.Light
        });
      }
    } catch (err) {
      console.warn('StatusBar init failed:', err);
    }
  }

  private async dismissBootSplash(): Promise<void> {
    if (this.bootSplashDismissed && !this.splashVisible) {
      return;
    }

    this.bootSplashDismissed = true;
    this.splashVisible = false;

    this.ensureBootSplashDom();

    // Hide the native splash immediately after Login is painted.
    await this.hideNativeSplash();

    const overlay = document.getElementById('app-boot-splash');
    if (overlay) {
      overlay.remove();
    }

    document.documentElement.classList.remove('app-booting');

    void this.checkPreviousAuthorization();
  }

  private updateToolbarVisibility(url: string) {
    this.routeAllowsToolbar = this.mapToolbarRoutes.some((route) => url.includes(route));
    this.refreshToolbar();
  }

  private refreshToolbar() {
    this.showMapToolbar = this.routeAllowsToolbar && !this.toolbarHidden;
  }

  async checkPreviousAuthorization() {
    const hadToken = !!localStorage.getItem('id_token');
    if (!this.currentUser && hadToken) {
      const toast = await this.toastController.create({
        message: this.translate.instant('APP.SESSION_EXPIRED'),
        duration: 4000,
        position: 'top',
        color: 'warning'
      });
      await toast.present();
    }
  }

  async presentExitAlert() {
    const alert = await this.alertController.create({
      header: this.translate.instant('APP.EXIT_TITLE'),
      message: this.translate.instant('APP.EXIT_MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('APP.CANCEL'),
          role: 'cancel',
          handler: () => {
            this.alertController.dismiss();
          }
        },
        {
          text: this.translate.instant('APP.QUIT'),
          handler: () => {
            navigator['app'].exitApp();
          }
        }
      ]
    });
    await alert.present();
  }
}